import { LoadingButton } from '@mui/lab';
import {
  Box,
  ButtonBase,
  Grid,
  InputAdornment,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import Modal from 'components/Modal';
import WrapperStepper from './WrapperStepper';
import PriceInput from 'components/PriceInput';
import { useEffect, useMemo, useState } from 'react';
import useIsMobileView from 'hooks/useIsMobileView';
import { actionButtonBg } from 'utils/helpers/actionButtonBg';
import moment from 'moment-jalaali';
import { useForm } from 'react-hook-form';
import { VALUE_INVESTMENT_FUND } from 'pages/Wallet/constants';
import useDispatch from 'hooks/useDispatch';
import { getInvestmentFixedCurrentlyInfo, getInvestmentFixedCurrentlyList, getInvestmentPortfolioBuyAmountMax, investmentPortfolioBuy } from 'store/slices/investment/investmentSlice';
import { repeat } from 'utils/helpers/interval';
import NumberFormats from 'components/NumberFormats';
import { checkIfRulesAreAccepted } from 'utils/helpers/checkIfRulesAreAccepted';
import useNotify from 'hooks/useNotify';
import useAlertModal from 'hooks/useAlertModal';
import { useNavigate } from 'react-router-dom';

let getInvestmentPortfolioBuyAmountMaxInterval;

function SubscriptionPlanModal({
  open,
  onClose,
  setOpenModalSubscriptionSuccessful,
  activeItem,
}) {
  const theme = useTheme();
  const notify = useNotify();
  const dispatch = useDispatch()
  const alert = useAlertModal();
  const navigate = useNavigate();
  const isMobileView = useIsMobileView();

  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [investmentPortfolioBuyAmountMax, setInvestmentPortfolioBuyAmountMax] = useState();

  const methods = useForm({
    defaultValues: {
      amount: '',
      typeId: VALUE_INVESTMENT_FUND?.Fixed,
      id: activeItem?.id,
    },
  });

  const { watch, setValue, getValues } = methods;

  const amount = watch('amount')
  const setErrorForMinimumAmount = useMemo(() => {
    if (
      amount &&
      +investmentPortfolioBuyAmountMax?.minAmount > +amount
    ) {
      return `حداقل مقدار سرمایه‌گذاری ${activeItem?.baseCoinName} ${investmentPortfolioBuyAmountMax?.minAmount}  می‌باشد.`;
    }
  }, [investmentPortfolioBuyAmountMax?.minAmount, amount]);

  const onSubmit = async () => {
    setLoading(true);

    const body = getValues();

    try {
      await dispatch(investmentPortfolioBuy(body), 'درخواست شما با موفقیت ارسال شد');

      dispatch(getInvestmentFixedCurrentlyList());
      await dispatch(getInvestmentFixedCurrentlyInfo());

      setOpenModalSubscriptionSuccessful();
      onClose();
    } catch (error) {
      checkIfRulesAreAccepted({ error, notify, alert, navigate });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeItem) return;

    const contractStart = moment().format('jYYYY/jMM/jDD HH:mm:ss');
    const contractEnd = moment()
      .add(activeItem.dayInvestment || 0, 'days')
      .format('jYYYY/jMM/jDD HH:mm:ss');

    setSteps([
      { label: 'شروع قرارداد', description: contractStart },
      { label: 'پایان قرارداد', description: contractEnd },
    ]);
  }, [activeItem]);

  const handleGetInvestmentPortfolioBuyAmountMax = async () => {
    const { payload } = await dispatch(
      getInvestmentPortfolioBuyAmountMax(activeItem?.portfolioId)
    );

    setInvestmentPortfolioBuyAmountMax(payload?.data?.data);
  };

  useEffect(() => {
    if (activeItem?.validSeconds) {
      clearInterval(getInvestmentPortfolioBuyAmountMaxInterval);

      handleGetInvestmentPortfolioBuyAmountMax();

      getInvestmentPortfolioBuyAmountMaxInterval = repeat({
        action: () => handleGetInvestmentPortfolioBuyAmountMax(),
        time: activeItem?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getInvestmentPortfolioBuyAmountMaxInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      maxWidth={'md'}
      onClose={onClose}
      open={open}
      title="Subscription Plan"
      dir="ltr"
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={5}>
          <BoxStyle>
            <Typography variant="h5" color="text.secondary" mb={3}>
              Duration (Days)
            </Typography>
            <Box display="flex" alignItems={isMobileView ? 'start' : 'center'}>
              <Grid container spacing={1}>
                <Grid item xs={6} md={3}>
                  <BoxFlexible component={ButtonBase} active={true}>
                    <Typography
                      variant="h4"
                      fontWeight={500}
                      color="secondary.main"
                    >
                      <BoxIcon>
                        <ion-icon name="checkmark"></ion-icon>
                      </BoxIcon>

                      {activeItem?.dayInvestment}
                    </Typography>
                  </BoxFlexible>
                </Grid>
              </Grid>
            </Box>

            <Box pt={6}>
              <Typography variant="h6" fontWeight={300} color="text.secondary">
                Subscription Amount
              </Typography>

              <PriceInput
                showPrefix={false}
                placeholder="Enter amount"
                value={watch('amount')}
                onChange={(e) => setValue('amount', e)}
                max={investmentPortfolioBuyAmountMax?.maxAmount}
                min={investmentPortfolioBuyAmountMax?.minAmount}
                maxDecimal={investmentPortfolioBuyAmountMax?.decimalPlacesAmount}
                coinName={activeItem?.baseCoinName}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography mr={1} variant="h5" color="text.secondary">
                        {activeItem?.baseCoinName}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                error={!!setErrorForMinimumAmount}
                helperText={setErrorForMinimumAmount}
              />
            </Box>

            <Box pt={2}>
              {investmentPortfolioBuyAmountMax?.availableBalance &&
                <Typography variant="subtitle1" component="p" color="text.secondary">
                  Available Balance Spot:{" "}
                  <Typography
                    variant="h6"
                    component="b"
                    color="text.main"
                    fontWeight={600}
                  >
                    {" "}
                    <NumberFormats
                      value={investmentPortfolioBuyAmountMax?.availableBalance}
                    />{" "}
                  </Typography>
                  {activeItem?.baseCoinName}
                </Typography>
              }

              {investmentPortfolioBuyAmountMax?.minAmount &&
                <Typography variant="subtitle1" component="p" color="text.secondary">
                  Minimum:{" "}
                  <Typography
                    variant="h6"
                    component="b"
                    color="text.main"
                    fontWeight={600}
                  >
                    {" "}
                    <NumberFormats
                      value={investmentPortfolioBuyAmountMax?.minAmount}
                    />{" "}
                  </Typography>
                  {activeItem?.baseCoinName}
                </Typography>
              }
            </Box>
          </BoxStyle>
        </Grid>

        <Grid item xs={12} md={7} display={"flex"} flexDirection={"column"} justifyContent={"space-between"}>
          <Box padding={isMobileView ? 0 : 5} py={isMobileView ? 5 : 0}>
            <WrapperStepper steps={steps} />
          </Box>

          <Box>

            <Box
              display="flex"
              alignItems="center"
              px={5}
              mt={2}
              justifyContent="space-between"
              flexDirection={"row"}
              gap={2}
            >
              <Typography variant="h4" fontWeight={300} color="text.main">
                Variable Profit
              </Typography>

              <Typography
                fontWeight={500}
                variant={isMobileView ? 'h5' : 'h2'}
                color="secondary.main"
              >
                {activeItem?.customerVariableProfitPercent}%
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              px={5}
              mt={2}
              justifyContent="space-between"
              flexDirection={"row"}
              gap={2}
            >
              <Typography variant="h4" fontWeight={300} color="text.main">
                Fixed Profit
              </Typography>

              <Typography
                fontWeight={500}
                variant={isMobileView ? 'h5' : 'h2'}
                color="secondary.main"
              >
                {activeItem?.customerFixedProfitPercent}%
              </Typography>
            </Box>

            <Box pt={4}>
              <LoadingButton
                color={actionButtonBg(theme)}
                fullWidth
                variant="contained"
                type="submit"
                loading={loading}
                onClick={onSubmit}
              >
                Subscribe
              </LoadingButton>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Modal>
  );
}
const BoxStyle = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRight: `1px solid ${theme.palette.modules.border}`,
  height: '100%',
  [theme.breakpoints.down('md')]: {
    borderRight: 'none',
  },
}));

const BoxIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  color: theme.palette.primary.main,
  right: 0,
  top: 0,
}));

const BoxFlexible = styled(Box)(({ theme, active }) => ({
  background: active
    ? theme.palette.background.activeCart
    : theme.palette.background.primaryGradient,
  '&:before': active && {
    content: "''",
    position: 'absolute',
    borderTop: '30px solid rgb(111 203 239)',
    borderLeft: '30px solid transparent',
    right: 0,
    top: 0,
    transition: 'all .4s ease-in-out',
    borderRadius: '0px 5px 0 0',
  },
  border: `1px solid ${theme.palette.modules.border}`,
  position: 'relative',
  width: '100%',
  padding: theme.spacing(1),
  borderRadius: '5px',
}));

export default SubscriptionPlanModal;
